// Commercial Kitchen Calculator — transparent planning model.
// All assumptions live in CALC_CONFIG so they can be tuned without touching logic.

export const CALC_CONFIG = {
  complexityWeight: { low: 1, medium: 2, high: 3 },
  prepWeight: { batch: 1, mixed: 1.5, cook_to_order: 2 },
  sharedIngredientsFactor: 0.9, // cross-utilisation reduces effective complexity
  coversPerStaff: 85, // daily covers one production staff member supports at medium complexity
  baseCrew: 3, // minimum viable kitchen crew
  breadthCap: 60, // SKU count at which breadth score saturates
  bands: { low: 34, medium: 67 }, // complexity index thresholds (0–100)
  highSkuThreshold: 12, // per-category SKU count flagged as a pressure point
  cookToOrderShareHigh: 0.5,
  surgeFactor: { steady: 1, moderate: 1.12, severe: 1.25 },
  bandHeadroom: { LOW: 2, MEDIUM: 3, HIGH: 4 },
};

export const BUSINESS_TYPES = [
  "Restaurant", "Café", "QSR", "Cloud Kitchen", "Food Brand", "Bakery", "Catering", "Other",
];

export const SERVICE_MODELS = ["Dine-in", "Takeaway", "Delivery", "Mixed"];

export const MENU_CATEGORIES = [
  { key: "starters", label: "Starters" },
  { key: "mains", label: "Main Course" },
  { key: "desserts", label: "Desserts" },
  { key: "drinks", label: "Drinks" },
  { key: "breads", label: "Breads" },
  { key: "rice", label: "Rice" },
  { key: "sides", label: "Sides" },
  { key: "other", label: "Other" },
];

export const COST_CONFIG = {
  // Monthly salaries (industry averages, adjustable)
  lineChefSalary: { LOW: 15000, MEDIUM: 22000, HIGH: 30000 }, // per line chef, by menu complexity
  headChefSalary: { LOW: 40000, MEDIUM: 60000, HIGH: 75000 }, // head chef — only without Skope
  supportStaffSalary: 12000, // helpers / stewards / dishwashers
  managerAndProcurement: 50000, // restaurant/store manager + procurement — only without Skope
  chefShare: 0.6, // share of total manpower that are chefs
  skopeSubscription: { LOW: 75000, MEDIUM: 110000, HIGH: 150000 }, // Skope monthly subscription (75k–150k range)
  // One-time capital — only without Skope (Skope kitchen + specialised equipment comes via collaboration)
  kitchenBuildBase: 500000,
  kitchenBuildPerStation: 175000,
  equipmentBase: 300000,
  equipmentPerStation: 90000,
  capexSpread: 0.15, // ±15% band on capital estimates
};

export function computeCosts(plan) {
  const C = COST_CONFIG;
  const band = plan.complexityBand;
  const stations = plan.stations.length;

  const capexMid =
    C.kitchenBuildBase + C.kitchenBuildPerStation * stations +
    C.equipmentBase + C.equipmentPerStation * stations;
  const capexLow = Math.round(capexMid * (1 - C.capexSpread));
  const capexHigh = Math.round(capexMid * (1 + C.capexSpread));

  const monthly = (mp) => {
    const chefs = Math.max(1, Math.round(mp * C.chefShare));
    const support = Math.max(0, mp - chefs);
    const without =
      C.headChefSalary[band] +
      chefs * C.lineChefSalary[band] +
      support * C.supportStaffSalary +
      C.managerAndProcurement;
    // With Skope: brand pays line chefs only — head chef, manager, procurement,
    // support and equipment are covered by the subscription.
    const withSkope = chefs * C.lineChefSalary[band] + C.skopeSubscription[band];
    return { without, withSkope };
  };

  const lo = monthly(plan.manpowerLow);
  const hi = monthly(plan.manpowerHigh);

  const savingsMonthlyLow = lo.without - lo.withSkope;
  const savingsMonthlyHigh = hi.without - hi.withSkope;

  return {
    band,
    capexLow,
    capexHigh,
    withoutMonthlyLow: lo.without,
    withoutMonthlyHigh: hi.without,
    withMonthlyLow: lo.withSkope,
    withMonthlyHigh: hi.withSkope,
    subscription: C.skopeSubscription[band],
    savingsMonthlyLow,
    savingsMonthlyHigh,
    savingsYearOneLow: savingsMonthlyLow * 12 + capexLow,
    savingsYearOneHigh: savingsMonthlyHigh * 12 + capexHigh,
  };
}

export const emptyMenu = () =>
  Object.fromEntries(
    MENU_CATEGORIES.map((c) => [c.key, { items: 0, complexity: "medium", prep: "mixed" }])
  );

export function computeKitchenPlan(input) {
  const C = CALC_CONFIG;
  const menu = input.menu || {};
  const active = MENU_CATEGORIES.filter((c) => (menu[c.key]?.items || 0) > 0);

  const totalItems = active.reduce((s, c) => s + menu[c.key].items, 0);
  let weighted = active.reduce(
    (s, c) =>
      s +
      menu[c.key].items *
        C.complexityWeight[menu[c.key].complexity] *
        C.prepWeight[menu[c.key].prep],
    0
  );
  if (input.sharedIngredients) weighted *= C.sharedIngredientsFactor;

  const avgComplexity = totalItems > 0 ? weighted / totalItems : 1; // 1..6
  const depthScore = ((avgComplexity - 1) / 5) * 100;
  const breadthScore = (Math.min(totalItems, C.breadthCap) / C.breadthCap) * 100;
  const complexityIndex = Math.round(depthScore * 0.65 + breadthScore * 0.35);
  const complexityBand =
    complexityIndex < C.bands.low ? "LOW" : complexityIndex < C.bands.medium ? "MEDIUM" : "HIGH";

  const ctoItems = active.reduce(
    (s, c) => (menu[c.key].prep === "cook_to_order" ? s + menu[c.key].items : s), 0
  );
  const ctoShare = totalItems > 0 ? ctoItems / totalItems : 0;

  const surge = C.surgeFactor[input.peakPattern] || 1;
  const volumeStaff =
    (input.dailyOrders / C.coversPerStaff) * (0.7 + (avgComplexity / 6) * 0.6);
  const core = C.baseCrew + volumeStaff * surge;
  const manpowerLow = Math.max(C.baseCrew, Math.round(core));
  const manpowerHigh =
    manpowerLow + C.bandHeadroom[complexityBand] + (input.shifts > 1 ? 1 : 0);

  const stations = ["Preparation", "Hot Kitchen"];
  if (menu.starters?.items > 0 || menu.desserts?.items > 0) stations.push("Cold Preparation");
  if (menu.breads?.items > 0) stations.push("Tandoor / Bakery");
  if (menu.rice?.items > 0) stations.push("Rice / Bulk Cooking");
  if (menu.drinks?.items > 0) stations.push("Beverage");
  stations.push("Plating / Pass");
  if (input.serviceModel === "Delivery" || input.serviceModel === "Mixed" || input.serviceModel === "Takeaway")
    stations.push("Packing / Dispatch");
  stations.push("Dishwashing", "Storage");

  const pressurePoints = [];
  active.forEach((c) => {
    if (menu[c.key].items > C.highSkuThreshold)
      pressurePoints.push(`High ${c.label.toLowerCase()} SKU count (${menu[c.key].items} items)`);
  });
  if (ctoShare > C.cookToOrderShareHigh)
    pressurePoints.push("High cooking-to-order dependency — the pass becomes the pacing point");
  if (input.peakPattern === "severe")
    pressurePoints.push("High peak-hour demand — design for the peak, not the average");
  if (active.filter((c) => menu[c.key].complexity === "high").length >= 3)
    pressurePoints.push("Broad high-complexity menu across multiple categories");
  if (!input.sharedIngredients && active.length >= 3)
    pressurePoints.push("Low ingredient cross-utilisation — higher prep and storage load");
  if (input.shifts > 1) pressurePoints.push("Multi-shift coordination and handover discipline");
  if (input.growthIntent !== "single")
    pressurePoints.push("Replication requirement — standardise recipes and stations early");
  if (pressurePoints.length === 0)
    pressurePoints.push("No critical pressure points detected at this menu scale");

  const observations = [
    `Menu spans ${active.length} ${active.length === 1 ? "category" : "categories"} with ${totalItems} total SKUs.`,
    `${Math.round(ctoShare * 100)}% of the menu is cooked to order — this drives line staffing at peak.`,
    input.peakPattern === "severe"
      ? "Severe peak trading means capacity, not average demand, should size the hot line."
      : "Demand pattern allows batch preparation to absorb much of the peak load.",
    input.growthIntent !== "single"
      ? "Multi-outlet intent: document SOPs and station logic now, not after outlet two."
      : "Single-outlet focus: optimise workflow before adding menu breadth.",
  ];

  return {
    complexityIndex,
    complexityBand,
    manpower: `${manpowerLow}–${manpowerHigh} people`,
    manpowerLow,
    manpowerHigh,
    stations,
    pressurePoints,
    observations,
    totalItems,
    activeCategories: active.length,
    nextStep:
      "Book a professional assessment — we map your recipes, volumes and service model onto an actual kitchen plan before you commit capital.",
    summary: {
      businessType: input.businessType,
      dailyOrders: input.dailyOrders,
      serviceModel: input.serviceModel,
    },
  };
}
